import { endOfDay, parse, startOfDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useData as useVikeData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';

import { useDeepMemo } from '@/renderer/utils/hooks/useDeepMemo';
import useMe from '@/renderer/utils/useMe';
import type { ResultsFilters, ResultsFormFilters } from './FilterTypes';
import Filters from './Filters';
import { ResultsContents } from './ResultsContents';
import { ResultsHeader } from './ResultsHeader';
import { SPORT_IDS, type SportName } from './consts';
import { useResultsSummary } from './useResultsSummary';

const CFB_FILTERS: ResultsFilters = {
  sportId: '1',
  startDate: new Date('2021-07-01'),
  endDate: new Date('2025-01-22'),
  playType: 'full' as const,
};

const NFL_FILTERS: ResultsFilters = {
  sportId: '3',
  startDate: new Date('2021-07-01'),
  endDate: new Date('2025-02-09'),
  playType: 'full' as const,
};

const EMPTYOBJ = {};

// The My/All scope the visitor is currently looking at, remembered for the lifetime of
// the SPA session. Changing sport or season is a real navigation, which remounts this
// component; without this the scope would reset to the route's default every time and
// then flip back to My, rendering the other scope's data for a beat (#2004).
let rememberedScope: 'me' | 'archive' | null = null;

export function Results({
  userId: baseUserId,
  title: pageTitle = 'Results',
  baseUrl = '/results',
  breakdown = undefined,
  hideHeader = false,
}: {
  userId: API.Id | 'me' | 'archive';
  baseUrl?: string;
  title?: string;
  breakdown?: string;
  hideHeader?: boolean;
}) {
  const { urlParsed, routeParams } = usePageContext();
  const data = useVikeData() as
    | {
        seasons: API.Season[];
        seasonsSimple: {
          last5: API.Season[];
          first: API.Season;
          last: API.Season;
          selected: API.Season[];
        };
      }
    | undefined;

  const search = useDeepMemo(urlParsed.search || EMPTYOBJ);

  // The My/All toggle switches the data source live (client-only, no navigation): 'me'
  // reads the member's receipts, 'archive' the public release feed. The toggle only
  // appears for a signed-in user on the two public/member entry routes (#2004).
  const me = useMe();
  const canToggle = !!me?.role && (baseUserId === 'me' || baseUserId === 'archive');

  // Seed from the remembered scope so a sport/season navigation doesn't reset to the
  // route's default and then flip back — that reset+flip rendered a full set of the
  // other scope's data for a beat, which read as a page flash on every chip click.
  const [userId, setUserId] = useState<API.Id | 'me' | 'archive'>(() =>
    rememberedScope && (baseUserId === 'me' || baseUserId === 'archive')
      ? rememberedScope
      : baseUserId,
  );

  // Default a signed-in visitor to their own results, even on the public /archive URL
  // (the toggle then lets them flip to All). Only until they touch the toggle.
  const setScope = (next: 'me' | 'archive') => {
    rememberedScope = next;
    setUserId(next);
  };
  useEffect(() => {
    if (!rememberedScope && me?.role && baseUserId === 'archive' && userId === 'archive') {
      // Remember the auto-default too, so the next navigation seeds straight into it
      // instead of rendering All and flipping again.
      rememberedScope = 'me';
      setUserId('me');
    }
  }, [me?.role, baseUserId, userId]);

  // How many pending plays this view has, reported up by PendingSection. `null` until it
  // settles, so nothing collapses on a guess.
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const sportId = routeParams.sportId
    ? SPORT_IDS[routeParams.sportId as SportName]
      ? `${SPORT_IDS[routeParams.sportId as SportName]}`
      : routeParams.sportId
    : SPORT_IDS[search.sportId as SportName]
      ? `${SPORT_IDS[search.sportId as SportName]}`
      : search.sportId || 'ras';

  const seasonId = routeParams.seasonId;

  const form = useForm<ResultsFormFilters>({
    defaultValues: {
      seasonId:
        routeParams.seasonId || (search.startDate || search.endDate ? undefined : 'current'),
      // convert 'nfl' in URL to '3' for form
      sportId,
      hidePropsFutures: !!search.playType && search.playType !== 'full',
      gameType: search.gameType || undefined,
    },
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const hidePropsFutures = form.watch('hidePropsFutures');

  const requestFilter = useMemo(() => {
    const res: ResultsFilters = {
      ...search,
      ...routeParams,
      sportId,
      // Supports a comma-separated list (e.g. ?gameType=fcs,extra) so a package
      // results link can scope to several play types at once.
      gameType: search.gameType
        ? (String(search.gameType).split(',').filter(Boolean) as API.GameTypes[])
        : undefined,
      startDate: search?.startDate
        ? startOfDay(parse(search.startDate, 'yyyyMMdd', new Date()))
        : undefined,
      endDate: search?.endDate
        ? endOfDay(parse(search.endDate, 'yyyyMMdd', new Date()))
        : undefined,
      playType:
        (search.playType as API.PlayTypes | 'full' | 'st' | 'pf' | 'hp') ||
        (hidePropsFutures ? ('hp' as const) : undefined),
    };

    if (!res.startDate && !res.endDate && seasonId !== 'all-time') {
      const endDate = data?.seasonsSimple?.selected?.[0]?.endDate;
      res.endDate = endDate ? new Date(endDate) : undefined;

      const startDate =
        data?.seasonsSimple?.selected?.[data?.seasonsSimple?.selected?.length - 1]?.startDate;
      res.startDate = startDate ? new Date(startDate) : undefined;
    }

    return res;
  }, [routeParams, search, data?.seasonsSimple, seasonId, hidePropsFutures, sportId]);

  const activeFilters =
    breakdown === 'cfb' ? CFB_FILTERS : breakdown === 'nfl' ? NFL_FILTERS : requestFilter;

  // "This view has nothing at all" — no graded plays AND no pending. It's a normal state
  // (a CFB member tapping CBB hits it every time), not an error, but it used to render a
  // 0-0 record over an empty chart, controls for a list that wasn't there and a legend
  // for nothing. Keep the honest record; drop the rest (#2004). Shares the summary
  // request with the header, so this costs no extra fetch.
  const summary = useResultsSummary(userId, activeFilters);
  const hasNoPlays =
    !summary.isLoading &&
    summary.wins + summary.losses + summary.pushes === 0 &&
    pendingCount === 0;

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col overflow-auto">
      {/* Keep the app Header bar; the old sport/season nav block is hidden (`hide`) in
          favor of the redesigned header card below (#2004). */}
      <Filters
        baseUrl={baseUrl}
        form={form}
        hide
        hideHeader={hideHeader}
        title={pageTitle}
        sportId={routeParams.sportId || search.sportId}
        seasonId={seasonId}
      />
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
        {!breakdown && (
          <ResultsHeader
            baseUrl={baseUrl}
            userId={userId}
            filters={activeFilters}
            sportId={routeParams.sportId || (search.sportId as string) || undefined}
            seasonId={seasonId}
            seasons={data?.seasons}
            scope={userId === 'archive' ? 'archive' : 'me'}
            onScopeChange={setScope}
            canToggle={canToggle}
            hasNoPlays={hasNoPlays}
          />
        )}
        <ResultsContents
          baseUrl={baseUrl}
          form={form}
          userId={userId}
          filters={activeFilters}
          breakdown={breakdown}
          title={pageTitle}
          hasNoPlays={hasNoPlays}
          onPendingCount={setPendingCount}
        />
      </div>
    </div>
  );
}
